import React from 'react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Package, Trash2 } from 'lucide-react';
import { useToast } from '../../hooks/use-toast';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const CompletedOrdersTab = ({ completedOrders, onOrdersUpdate }) => {
  const { toast } = useToast();

  const deleteOrder = async (orderId) => {
    if (!window.confirm('¿Estás seguro de eliminar este pedido?')) return;
    
    try {
      const response = await fetch(`${API}/orders/${orderId}`, { method: 'DELETE' });
      if (response.ok) {
        toast({ title: "Éxito", description: "Pedido eliminado correctamente" });
        onOrdersUpdate();
      }
    } catch (error) {
      toast({ title: "Error", description: "No se pudo eliminar el pedido", variant: "destructive" });
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      delivered: 'bg-green-600',
      cancelled: 'bg-red-600'
    };
    const labels = {
      delivered: 'Entregado',
      cancelled: 'Cancelado'
    };
    return <Badge className={styles[status] || 'bg-slate-600'}>{labels[status] || status}</Badge>;
  };

  const getPaymentStatusBadge = (status) => {
    const styles = {
      pagado: 'bg-emerald-600',
      pendiente: 'bg-yellow-600',
      local: 'bg-blue-600'
    };
    return <Badge className={styles[status] || 'bg-slate-600'}>{status}</Badge>;
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold text-white">Pedidos Completados</h2>
        <Badge className="bg-slate-700 text-lg px-4 py-2">
          {completedOrders.length} pedidos
        </Badge>
      </div>

      {completedOrders.length === 0 ? (
        <Card className="bg-slate-800/50 backdrop-blur-sm border-slate-700">
          <CardContent className="p-12 text-center">
            <Package className="h-16 w-16 text-slate-600 mx-auto mb-4" />
            <p className="text-slate-400 text-lg">No hay pedidos completados</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4">
          {completedOrders.map((order) => (
            <Card key={order.id} className="bg-slate-800/50 backdrop-blur-sm border-slate-700">
              <CardContent className="p-6">
                <div className="flex justify-between items-start mb-4">
                  <div>
                    <div className="flex items-center gap-2 mb-2">
                      <h3 className="text-lg font-semibold text-white">{order.customer_name}</h3>
                      {getStatusBadge(order.status)}
                      {getPaymentStatusBadge(order.payment_status)}
                    </div>
                    <p className="text-slate-400 text-sm">ID: {order.order_id}</p>
                    <p className="text-slate-400 text-sm">{order.customer_email}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-2xl font-bold text-white">
                      ${order.total_amount.toLocaleString()}
                    </p>
                    <p className="text-slate-400 text-sm">{order.payment_method}</p>
                  </div>
                </div>

                <div className="space-y-2 mb-4">
                  {order.items.map((item, idx) => (
                    <div key={`${order.id}-item-${idx}-${item.name}`} className="flex justify-between text-sm bg-slate-900/50 p-2 rounded">
                      <span className="text-slate-300">{item.name} x{item.quantity}</span>
                      <span className="text-white font-medium">${item.price.toLocaleString()}</span>
                    </div>
                  ))}
                </div>

                <div className="flex justify-between items-center pt-4 border-t border-slate-700">
                  <p className="text-slate-400 text-sm">
                    {new Date(order.created_at).toLocaleString('es-AR')}
                  </p>
                  <Button
                    variant="destructive"
                    size="sm"
                    onClick={() => deleteOrder(order.id)}
                  >
                    <Trash2 className="h-4 w-4 mr-1" />
                    Eliminar
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default CompletedOrdersTab;
